// Fase 15A — Utenti piattaforma: lista con ricerca/filtri e azioni di stato
// (punti 44-47). Mai dati di sessione o secret.
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUS_STYLE = {
  ACTIVE: "bg-green-50 text-green-800 border-green-300",
  INVITED: "bg-sky-50 text-sky-800 border-sky-300",
  SUSPENDED: "bg-red-50 text-red-800 border-red-300",
  DISABLED: "bg-slate-100 text-slate-500 border-slate-300",
  ARCHIVED: "bg-slate-100 text-slate-500 border-slate-300",
};

export default function PlatformUsers() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [skip, setSkip] = useState(0);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const limit = 25;

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/platform/users", {
        params: { search: search || undefined, status: status || undefined,
                  skip, limit } });
      setData(d);
      setError(null);
    } catch (e) {
      setError(formatApiError(e));
    }
  }, [search, status, skip]);

  useEffect(() => { load(); }, [load]);

  const setUserStatus = async (u, s) => {
    try {
      await api.put(`/admin/platform/users/${u.id}/status`, null,
        { params: { status: s, reason: "Da console SuperAdmin" } });
      toast.success(`${u.email} → ${s}`, {
        description: s === "SUSPENDED" ? "Sessioni attive invalidate immediatamente." : undefined });
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  const hardDelete = async () => {
    try {
      await api.delete(`/admin/platform/users/${deleteTarget.id}`);
      toast.success(`${deleteTarget.email} eliminato definitivamente`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      toast.error("Errore", { description: formatApiError(e) });
    }
  };

  if (error) return <p className="text-sm text-red-700" data-testid="users-error">{error}</p>;
  if (!data) return <p className="text-sm text-slate-600" data-testid="users-loading">Caricamento…</p>;

  return (
    <div className="space-y-6" data-testid="platform-users-page">
      <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
        <Users2 className="w-7 h-7 text-green-800" aria-hidden="true" /> Utenti
      </h1>

      <div className="flex flex-wrap gap-2">
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setSkip(0); }}
          placeholder="Cerca per nome o email…" className="w-64 h-9"
          data-testid="users-search-input" />
        <Select value={status} onValueChange={(v) => { setStatus(v); setSkip(0); }}>
          <SelectTrigger className="w-44 h-9" data-testid="users-status-filter">
            <SelectValue placeholder="Tutti gli stati" /></SelectTrigger>
          <SelectContent>
            {["ACTIVE", "INVITED", "SUSPENDED", "DISABLED", "ARCHIVED"].map((s) => (
              <SelectItem key={s} value={s}>{s}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="users-list">
        {data.items.length === 0 && (
          <p className="text-sm text-slate-500">Nessun utente trovato.</p>)}
        {data.items.map((u) => (
          <div key={u.id} className="rounded-lg border border-slate-200 p-3 flex flex-wrap items-center gap-3"
            data-testid={`user-${u.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-slate-900">
                {u.full_name}
                {u.is_superadmin && (
                  <Badge variant="outline" className="ml-2 text-[9px] bg-indigo-50 text-indigo-800 border-indigo-300">SUPER ADMIN</Badge>)}
              </div>
              <p className="text-[11px] text-slate-500">{u.email}</p>
            </div>
            <Badge variant="outline" className={STATUS_STYLE[u.status || "ACTIVE"]}
              data-testid={`user-status-${u.id}`}>{u.status || "ACTIVE"}</Badge>
            {!u.is_superadmin && (
              <Select onValueChange={(v) => setUserStatus(u, v)}>
                <SelectTrigger className="h-8 w-40 text-xs" data-testid={`user-action-${u.id}`}>
                  <SelectValue placeholder="Azione…" /></SelectTrigger>
                <SelectContent>
                  {(u.status || "ACTIVE") !== "ACTIVE" && (
                    <SelectItem value="ACTIVE">Riattiva</SelectItem>)}
                  {(u.status || "ACTIVE") === "ACTIVE" && (
                    <SelectItem value="SUSPENDED">Sospendi</SelectItem>)}
                  {(u.status || "ACTIVE") !== "DISABLED" && (
                    <SelectItem value="DISABLED">Disabilita</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Link to={`/admin/platform/utenti/${u.id}`}
              className="text-xs text-green-700 hover:underline"
              data-testid={`user-detail-${u.id}`}>Dettaglio →</Link>
            {!u.is_superadmin && (
              <Button size="sm" variant="outline"
                className="h-7 text-xs text-red-700 border-red-200 hover:bg-red-50"
                onClick={() => setDeleteTarget(u)}
                data-testid={`user-delete-${u.id}`}>
                <Trash2 className="w-3 h-3" aria-hidden="true" /></Button>
            )}
          </div>
        ))}
      </div>

      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Button variant="outline" size="sm" disabled={skip === 0}
          onClick={() => setSkip(Math.max(0, skip - limit))} data-testid="users-prev">←</Button>
        <span>{skip + 1}–{Math.min(skip + limit, data.total)} di {data.total}</span>
        <Button variant="outline" size="sm" disabled={skip + limit >= data.total}
          onClick={() => setSkip(skip + limit)} data-testid="users-next">→</Button>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare definitivamente {deleteTarget?.full_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Verranno eliminati in modo irreversibile l'account ({deleteTarget?.email}),
              le membership, le assegnazioni come consulente/revisore, gli inviti e i dati
              collegati. L'operazione non è annullabile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-delete-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction className="bg-red-700 hover:bg-red-800" onClick={hardDelete}
              data-testid="user-delete-confirm">Elimina definitivamente</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
